<template>
    <Modal id="trading-sessions-modal"
           size="lg"
           title="TRADING_SESSIONS_LIST.MODAL_TITLE"
           icon="fa-clipboard-list"
           :cancel="cancel">
        <div class="mb-1 btn-group btn-group-justified">
            <label class="btn btn-default"
                   :class="{'active': !showAllDevices}"
                   @click.prevent="setShowAllDevices(false)">
                {{ l('TRADING_SESSIONS_LIST.CURRENT_DEVICE_BTN') }}
            </label>
            <label class="btn btn-default"
                   :class="{'active': showAllDevices}"
                   @click.prevent="setShowAllDevices(true)">
                {{ l('TRADING_SESSIONS_LIST.ALL_DEVICES_BTN') }}
            </label>
        </div>

        <table class="table table-striped table-hover">
            <thead>
                <tr>
                    <th>
                        {{ l('TRADING_SESSIONS_LIST.ID') }}
                    </th>
                    <th v-if="showAllDevices"
                        class="no-wrap"
                        width="30%">
                        {{ l('TRADING_SESSIONS_LIST.DEVICE') }}
                    </th>
                    <th width="30%"
                        class="no-wrap">
                        {{ l('TRADING_SESSIONS_LIST.STARTED_AT') }}
                    </th>
                    <th width="30%"
                        class="no-wrap">
                        {{ l('TRADING_SESSIONS_LIST.ENDED_AT') }}
                    </th>
                    <th width="10%"
                        class="no-wrap"
                        style="padding-right:0;">
                        {{ l('TRADING_SESSIONS_LIST.CASHED_UP') }}
                    </th>
                    <th width="5%"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="tradingSession in sortedFilteredSessions"
                    :key="tradingSession.ID"
                    @click.prevent="selectSession(tradingSession)">
                    <td class="no-wrap">
                        {{ tradingSession.ID }}
                        <span v-if="tradingSession.ID === selectedTradingSessionID"
                              class="label label-success">{{ l('SELECTED') }}</span>
                    </td>
                    <td v-if="showAllDevices">
                        {{ tradingSession.device.name }}
                    </td>
                    <td>
                        {{ dateFilter(tradingSession.startedAt, false) }}
                        <br />
                        {{ timeFilter(tradingSession.startedAt, false) }}
                    </td>
                    <td>
                        <template v-if="tradingSession.endedAt">
                            {{ dateFilter(tradingSession.endedAt, false) }}
                            <br />
                            {{ timeFilter(tradingSession.endedAt, false) }}
                        </template>
                        <template v-else>
                            <em>{{ l('REPORTS.NOT_ENDED') }}</em>
                        </template>
                    </td>
                    <td>
                        <i v-if="tradingSession.tradingSessionStatusID === 3"
                           class="fa-solid fa-check"></i>
                    </td>
                    <th>
                        <a class="btn btn-primary"><i class="fa-solid fa-arrow-right"></i></a>
                    </th>
                </tr>
            </tbody>
        </table>
        <PageLoadingSpinner v-if="tradingSessionsLoading" />
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import TradingSessionCollection from '@/lib/TradingSessions/TradingSessionCollection';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'TradingSessionsListModal',

    components: { Modal, PageLoadingSpinner },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * Trading sessions for the current device.
         * These will already be loaded by ReportsView so can just be passed in.
         *
         * @type {TradingSessionCollection}
         */
        currentDeviceTradingSessionCollection: {
            required: true,
            type: TradingSessionCollection,
        },

        /**
         * Initial value of the "Show All Devices" toggle.
         * This is useful to set to true if the currently selected session on the reports view is already
         * from another device.
         */
        allDevices: {
            type: Boolean,
            default: false,
        },

        selectedTradingSessionID: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            showAllDevices: false,

            /**
             * Trading sessions for all devices.
             * This only gets loaded if the user clicked "Show All Devices".
             *
             * @type {TradingSessionCollection}
             */
            allDevicesTradingSessionCollection: null,

            tradingSessionsLoading: false,
        };
    },

    computed: {
        /**
         * @return {TradingSession[]}
         */
        sortedFilteredSessions() {
            let sessions;

            if (this.showAllDevices) {
                if (!this.allDevicesTradingSessionCollection) {
                    return [];
                }
                sessions = this.allDevicesTradingSessionCollection.getAll();
            } else {
                sessions = this.currentDeviceTradingSessionCollection.getAll();
            }

            sessions.sort((a, b) => b.startedAt - a.startedAt);

            return sessions;
        },
    },

    created() {
        if (this.allDevices) {
            // We could just set the initial value of showAllDevices to true,
            // but this needs to do more to load the trading sessions for those other devices.
            this.setShowAllDevices(true);
        }
    },

    methods: {
        setShowAllDevices(value) {
            this.showAllDevices = value;

            if (this.allDevicesTradingSessionCollection === null && !this.tradingSessionsLoading) {
                this.tradingSessionsLoading = true;
                container.tradingSessionManager.getSessions()
                    .then((allDevicesTradingSessionCollection) => {
                        this.allDevicesTradingSessionCollection = allDevicesTradingSessionCollection;
                    })
                    .finally(() => {
                        this.tradingSessionsLoading = false;
                    });
            }
        },

        selectSession(tradingSession) {
            this.resolve(tradingSession);
        },
    },
};
</script>

<style lang="less">
#trading-sessions-modal {
    table {
        th,
        td {
            vertical-align: middle;
        }

        tbody {
            tr {
                cursor: pointer;
            }
        }
    }
}
</style>
